/**
 * attendance/{id} - per-student-per-class-per-day record.
 *
 * Doc id is a composite key (offeringId_YYYY-MM-DD_studentId) so a
 * mark-click can address its record directly. First mark on a day is a
 * create; every later tap is a CORRECTION and must go through updateDoc,
 * because the rules pin recordedAt on update - see useMarkAttendance.
 *
 * Rules: read = active staff; write = active staff (recordedBy must
 * equal self). Director can delete; nobody else hard-deletes.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type FieldValue,
  type Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { useAuth } from './auth-context';
import { attendanceDocId } from './attendanceMarkId';
// A PURE `import type`, so type stripping ERASES the whole statement. Written
// with the inline `type` modifier it still emits a runtime import of a
// relative specifier with no extension, which node's --experimental-strip-types
// cannot resolve, and every plain-node test that loads this file pays for it.
import type { TonightAttendanceMark } from './tonight';
// THE EXTENSION IS LOAD-BEARING. A relative TypeScript specifier does not
// resolve under `node --experimental-strip-types` without it, and four of this
// repo's suites load this file that way. Same shape as subQuery.ts:21, which
// imports this exact symbol. CI caught the extensionless version on
// tests/jr-staff-own-attendance.test.mjs (2026-09-17). isoDaysAgo below reads
// today through this same helper rather than its own studioDayKey import, so
// this file has one "what day is it" source, not two.
import { studioTodayKey } from './practiceSummary.ts';
// EXPLICIT .ts, and it is load bearing. `import { type X } from` is NOT the
// same as `import type { X } from`: the inline form still EMITS a runtime
// import of the module once the type specifier is stripped, so under
// `node --experimental-strip-types` (which most of test:handlers runs under)
// an extensionless specifier fails with MODULE_NOT_FOUND. Proven by running
// both forms. Same shape as attendanceConcernFilingCore.ts's own import of
// this module.
import { type AttendanceConcernFlag } from './alternates.ts';
// The concern SCAN moved to the server (see useAttendanceConcerns below), so
// this file no longer imports the streak arithmetic or the offerings list it
// used to need: the only thing left that it takes from lib/alternates.ts is
// the flag's own shape, which the payload still carries field for field.
import { attendanceConcernsAreStale, loadAttendanceConcerns } from './attendanceConcernsClient.ts';
import { splitExplainedConcerns, type ConcernMissNote } from './attendanceConcernExplained.ts';
import { saveMergedAttendanceNote } from './attendanceNoteMerge.ts';
import { consecutiveWeekAbsenceFlags, type StudioAttendanceMark } from './attendanceIntegrity';

import { onCountableMarks } from './classCancellations';


export type { AttendanceConcernRule } from './alternates';

/**
 * THREE WORDS, AND NO FOURTH. Bobbie, 2026-08-23: "Excuses should be removed
 * entirely. Just absent late attended."
 *
 * This finishes what Kim started ("take off excused", 2026-08), which was only
 * half done: excused came off the marking chips but stayed in this type, in
 * STATUS_ORDER, and in the rules enum, because existing records carried it.
 * Those records are rewritten to absent by scripts/remove-excused-marks.mjs,
 * which must run BEFORE the tightened rules deploy - after the enum shrinks,
 * any correction to a still-excused mark is rejected by validation.
 *
 * The family absence-notice one-tap reply survives under her new name, "Mark
 * as parent submitted in advance", and now writes plain absent. Nothing is
 * lost by that: the fact the family told the studio ahead of time lives on the
 * absence notice itself, which staff can already see.
 *
 * DANCER attendance only. Jr staff marks (staffAttendanceMath.ts) keep their
 * own excused status on purpose - it means something different there (it
 * breaks the reliability streak instead of counting as a miss), the ruling
 * was given about what parents see, and quietly changing a child assistant's
 * reliability math would be a second decision she has not made. Asked, open.
 */
export type AttendanceStatus = 'present' | 'absent' | 'late';

export const STATUS_LABELS: Record<AttendanceStatus, string> = {
  present: 'Present',
  absent: 'Absent',
  late: 'Late',
};

export const STATUS_ORDER: AttendanceStatus[] = [
  'present',
  'late',
  'absent',
];

/**
 * Kept as an alias so the marking screen's callers read unchanged; with
 * excused gone the pickable set IS the status set.
 */
export const MARKABLE_STATUS_ORDER: AttendanceStatus[] = STATUS_ORDER;

/**
 * The one place a raw Firestore value becomes an AttendanceStatus.
 *
 * 'excused' maps to 'absent' rather than being dropped: the app runs
 * persistentLocalCache, so a device that last synced before the migration can
 * hold pre-rewrite documents, and dropping them would make a marked dancer
 * look unmarked - which hands `alreadyExists: false` to a record that exists
 * and gets the write denied. Anything unrecognised is null, never guessed.
 */
export function normalizeAttendanceStatus(value: unknown): AttendanceStatus | null {
  if (value === 'present' || value === 'absent' || value === 'late') return value;
  if (value === 'excused') return 'absent';
  return null;
}

/** A concern starts only after more than 3 absences in the same class. */
export const ATTENDANCE_CONCERN_MIN_ABSENCES = 4;

export interface AttendanceDoc {
  id: string;
  offeringId: string;
  date: string; // YYYY-MM-DD
  studentId: string;
  status: AttendanceStatus;
  notes: string;
  recordedBy: string;
  recordedAt: Timestamp | FieldValue;
  updatedAt: Timestamp | FieldValue;
}

// Split out to lib/attendanceMarkId.ts (zero imports, so it is testable
// without pulling in firebase/firestore) - re-exported here unchanged so
// every existing caller of `attendanceDocId` from this file keeps working.
export { attendanceDocId };

export interface UseAttendanceForClassDayResult {
  loading: boolean;
  /** Map keyed by studentId → AttendanceDoc */
  byStudent: Map<string, AttendanceDoc>;
  error: string | null;
  /**
   * True while the newest snapshot came from the on-device cache rather than
   * the server. The app runs persistentLocalCache, so a STALE cache can raise
   * a snapshot that is missing a mark somebody made on another device, and
   * `loading` is already false by then. Callers that are about to WRITE must
   * treat that as unconfirmed, or they hand `alreadyExists: false` to a record
   * that does exist and the create is denied. Starts true: before the first
   * snapshot we have not heard from anyone.
   */
  fromCache: boolean;
  /** True while a mark made on this device has not reached the server yet. */
  hasPendingWrites: boolean;
  /**
   * `${offeringId}|${date}` of the marks in `byStudent`, or null until the
   * joined marks (marks plus notes) for it have arrived. The reset on a new class or day happens in an effect, so
   * for one render the old day's marks sit beside the new key; a reader that
   * must not mix them (the Attendance page's open-time order) checks this.
   */
  loadedKey: string | null;
}

/**
 * Subscribe to every attendance row for a given offering + date. Keyed
 * by studentId so the page can look up "what did I last mark this kid
 * as?" in O(1).
 */
/**
 * THE ONE PLACE THAT KNOWS TEACHER NOTES LIVE SOMEWHERE ELSE.
 *
 * Bobbie, 2026-08-22: "Parents should only see absent or late or attend,
 * attended." Firestore grants documents, not fields, so the note had to leave
 * the family-readable mark and live on attendanceNotes/{sameId}.
 *
 * THAT MADE EVERY `mark.notes` READER A TRAP, and a review proved I had
 * already fallen into it: useClassAttendanceMarks in offerings.ts reads the
 * raw attendance document, so after the migration its `notes` was `''`
 * unconditionally - not a race, every time. RosterPanel feeds exactly that
 * object to the alternate step-in writer, which appends to what it was given,
 * so recording a step-in wrote `"Alternate covered: Jane"` over a note that
 * had said `"Mom asked us to watch her ankle."` The teacher's sentence was
 * silently deleted the first time anyone recorded a cover.
 *
 * So subscribing is a shared function rather than a thing each hook
 * remembers to do, and tests/attendance-notes-readers.test.mjs fails if a new
 * reader of attendance ever reaches for `.notes` without coming through here.
 *
 * @param onNotes receives id -> note text, id being the shared
 *   offeringId_date_studentId. Called with an empty map when the caller is not
 *   allowed to read them, which is the ordinary case for a family.
 * @param onDenied distinguishes "you may not see notes" (a family, expected)
 *   from any other failure (a missing index, an offline device), which a
 *   teacher DOES need to know about.
 */
export function subscribeAttendanceNotes(
  offeringId: string,
  bounds: { date: string } | { fromDate: string },
  onNotes: (notesById: Map<string, string>) => void,
  onDenied?: (reason: 'permission-denied' | 'error', message: string) => void
): () => void {
  if (!db) return () => {};
  const base = collection(db, 'attendanceNotes');
  const q = 'date' in bounds
    ? query(base, where('offeringId', '==', offeringId), where('date', '==', bounds.date))
    : query(base, where('offeringId', '==', offeringId), where('date', '>=', bounds.fromDate));
  return onSnapshot(
    q,
    (snap) => {
      const next = new Map<string, string>();
      for (const d of snap.docs) {
        const value = (d.data() as { notes?: unknown }).notes;
        if (typeof value === 'string' && value) next.set(d.id, value);
      }
      onNotes(next);
    },
    (err) => {
      onNotes(new Map());
      // A family being refused is the design, not a fault, and must never
      // raise a banner on their screen. Anything else is a real failure that
      // would otherwise show a teacher "no notes" for a class that has them.
      onDenied?.(err.code === 'permission-denied' ? 'permission-denied' : 'error', err.message);
    }
  );
}

/**
 * THE JOIN, LIFTED OUT OF THE HOOK SO IT CAN BE PROVED WITHOUT A BROWSER.
 *
 * Two listeners feed one map, and the rules about how they combine are the
 * difference between a teacher seeing the sentence they wrote and silently
 * writing over it. Buried in a useEffect, none of them had a test.
 *
 * NOTHING IS EMITTED UNTIL THE NOTES HAVE REPORTED ONCE, refusal included,
 * and that is not tidiness. LateNoteField seeds its useState from the note it
 * is handed and never re-reads the prop, so an emit carrying `notes: ''`
 * mounts an empty "Why? (optional)" for a dancer whose reason is on record,
 * and the next save puts the blank back. The marks arrive first in practice -
 * they come from the on-device cache, while attendanceNotes is a brand-new
 * collection with nothing cached - so this is the ordinary path, not an edge
 * case.
 *
 * A LATER setNotes REPLACES rather than merges: a note the teacher cleared
 * has to go back to '' on the mark, not linger from the previous snapshot.
 */
export function createAttendanceDayJoin(
  emit: (byStudent: Map<string, AttendanceDoc>) => void
): {
  setMarks: (next: Map<string, AttendanceDoc>) => void;
  setNotes: (next: Map<string, string>) => void;
  notesUnavailable: () => void;
} {
  let marks = new Map<string, AttendanceDoc>();
  let notesById = new Map<string, string>();
  let notesReported = false;
  const publish = () => {
    if (!notesReported) return;
    const merged = new Map<string, AttendanceDoc>();
    for (const [studentId, mark] of marks) {
      // Keyed on the MARK'S OWN doc id, which is the note document's id too:
      // both are attendanceDocId(offeringId, date, studentId).
      merged.set(studentId, { ...mark, notes: notesById.get(mark.id) ?? '' });
    }
    emit(merged);
  };
  return {
    setMarks: (next) => { marks = next; publish(); },
    setNotes: (next) => { notesById = next; notesReported = true; publish(); },
    notesUnavailable: () => { notesReported = true; publish(); },
  };
}

export function useAttendanceForClassDay(
  offeringId: string | undefined,
  date: string | undefined
): UseAttendanceForClassDayResult {
  const [byStudent, setByStudent] = useState<Map<string, AttendanceDoc>>(
    new Map()
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fromCache, setFromCache] = useState(true);
  const [hasPendingWrites, setHasPendingWrites] = useState(false);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);

  useEffect(() => {
    if (!db || !offeringId || !date) {
      setByStudent(new Map());
      setLoading(false);
      setFromCache(true);
      setLoadedKey(null);
      return;
    }
    // Clear before the new listener resolves, the same way the Jr staff lane
    // in staffAttendance.ts already does. Without this, changing the DATE with
    // the class unchanged keeps the previous day's map on screen: chips look
    // marked when they are not, and worse, the page hands `alreadyExists: true`
    // to a doc that does not exist on the new date, so the write takes the
    // update branch and fails with "No document to update".
    setByStudent(new Map());
    setLoading(true);
    setError(null);
    setFromCache(true);
    setLoadedKey(null);
    const q = query(
      collection(db, 'attendance'),
      where('offeringId', '==', offeringId),
      where('date', '==', date)
    );
    // THE TEACHER'S NOTES ARE A SECOND, STAFF-ONLY COLLECTION now (Bobbie,
    // 2026-08-22: "Parents should only see absent or late or attend,
    // attended"), so they are fetched separately and merged back onto each
    // mark in memory. Every staff screen that reads `mark.notes` therefore
    // keeps working untouched, and a family - who cannot read this collection
    // at all - simply never has one to merge.
    // The gate and the merge both live in createAttendanceDayJoin, which is
    // exported and proved in tests/attendance-notes-readers.test.mjs.
    // loadedKey is set WITH the joined map, never before it: the join holds
    // the marks back until the notes report, so a key set from the marks
    // snapshot alone would vouch for an empty map (Codex on #1412).
    const key = `${offeringId}|${date}`;
    const join = createAttendanceDayJoin((merged) => {
      setByStudent(merged);
      setLoadedKey(key);
    });
    const unsubNotes = subscribeAttendanceNotes(
      offeringId,
      { date },
      (next) => join.setNotes(next),
      (reason, message) => {
        // A family refused is the design and stays silent. Anything else is a
        // real failure, and a teacher shown "no notes" for a class that has
        // them would then write over them.
        if (reason !== 'permission-denied') setError(message);
        join.notesUnavailable();
      }
    );
    // includeMetadataChanges is load-bearing, not a nicety: without it, a
    // cache snapshot whose contents match the server raises no second event,
    // so fromCache would stay true forever and the page would never confirm.
    const unsub = onSnapshot(
      q,
      { includeMetadataChanges: true },
      (snap) => {
        const m = new Map<string, AttendanceDoc>();
        for (const d of snap.docs) {
          const data = d.data() as Omit<AttendanceDoc, 'id'>;
          // Both 2026-08-23 rulings meet on this one line: the status is
          // normalized (a legacy cached 'excused' folds into absent), and
          // notes start blank because the teacher's words live on
          // attendanceNotes now - createAttendanceDayJoin merges them in.
          const status = normalizeAttendanceStatus(data.status);
          if (!status) continue;
          m.set(data.studentId, { id: d.id, ...data, status, notes: '' });
        }
        join.setMarks(m);
        setFromCache(snap.metadata.fromCache);
        // A tap that has not reached the server yet: the Submit bar waits
        // for it before asking the server to clear the reminder.
        setHasPendingWrites(snap.metadata.hasPendingWrites);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
    return () => {
      unsub();
      unsubNotes();
    };
  }, [offeringId, date]);

  return { loading, byStudent, error, fromCache, hasPendingWrites, loadedKey };
}

/**
 * Thrown when a mark does not settle within ATTENDANCE_WRITE_TIMEOUT_MS.
 *
 * Bobbie, on a teacher in the studio with no signal: "Oh yes we need that
 * fixed!" Firestore's persistentLocalCache queues the write and updates the
 * chip on screen from the local copy right away, but the WRITE PROMISE
 * itself does not resolve until the server acknowledges it - offline, that
 * is never, so an `await` on the raw call would leave the row's busy state
 * stuck forever (the exact bug already fixed once for the announce list,
 * see AnnounceWriteTimeoutError in lib/offeringStaffNotes.ts; this is the
 * same shape of fix for a different write). The mark IS saved on this
 * device the moment the tap lands - that is what persistentLocalCache is
 * for - so this is deliberately NOT worded as a failure. Callers should
 * render it as calm, confirmed-locally copy, not as a rose error box.
 */
export class AttendanceWriteTimeoutError extends Error {
  readonly code = 'offline-queued' as const;
  constructor() {
    super('Saved on this phone. It will reach the studio when you have signal.');
    this.name = 'AttendanceWriteTimeoutError';
  }
}

/** How long a mark waits to hear back from the studio before the UI stops
 * waiting and reports it as locally saved instead. */
const ATTENDANCE_WRITE_TIMEOUT_MS = 10_000;

/**
 * Races a write against a fixed timeout so a tap's busy state always
 * resolves one way or the other. The underlying write is NOT cancelled on
 * timeout - Firestore has no cancel for an in-flight call - so it may still
 * reach the server later; the local copy already has it either way.
 */
export function withAttendanceWriteTimeout<T>(promise: Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(
      () => reject(new AttendanceWriteTimeoutError()),
      ATTENDANCE_WRITE_TIMEOUT_MS
    );
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        window.clearTimeout(timer);
        reject(err);
      }
    );
  });
}

/**
 * True for AttendanceWriteTimeoutError, including one that has crossed a
 * try/catch boundary as a plain rejected value rather than the class
 * instance (defensive - matches the isPermissionDeniedError pattern used
 * elsewhere in the codebase for the same reason).
 */
export function isAttendanceWriteTimeout(err: unknown): boolean {
  return err instanceof AttendanceWriteTimeoutError;
}

/**
 * Mark one dancer for one class on one day. Re-tapping corrects the mark.
 *
 * A correction is an updateDoc, NOT a fresh setDoc: the rules pin
 * `recordedAt` on update so "first recorded" survives later edits, which
 * means a setDoc that re-sends recordedAt: serverTimestamp() is DENIED on
 * every re-mark. That was live for a while - the first tap saved and every
 * correction threw "Missing or insufficient permissions" (Bobbie hit it on
 * staging, 2026-07-28). Same posture as useMarkStaffAttendance below in
 * lib/staffAttendance.ts, which had the branch from day one.
 *
 * `alreadyExists` comes from callers that hold the day's records (the
 * attendance page, the roster panel); the Assistant path does not, so when
 * it is undefined we spend one getDoc to find out rather than guess. The
 * whole body (the lookup AND the write) is wrapped in the write timeout,
 * because on a dead connection the lookup can hang just as easily as the
 * write.
 */
export function useMarkAttendance() {
  const { user } = useAuth();
  return async (input: {
    offeringId: string;
    date: string;
    studentId: string;
    status: AttendanceStatus;
    notes?: string;
    alreadyExists?: boolean;
  }): Promise<void> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    await withAttendanceWriteTimeout(
      (async () => {
        const id = attendanceDocId(input.offeringId, input.date, input.studentId);
        const ref = doc(db, 'attendance', id);
        const exists =
          typeof input.alreadyExists === 'boolean'
            ? input.alreadyExists
            : (await getDoc(ref)).exists();
        // THE NOTE IS A SEPARATE DOCUMENT (Bobbie, 2026-08-22: "Parents
        // should only see absent or late or attend, attended"). Firestore
        // grants documents, not fields, so the only way a family can read the
        // STATUS without also reading the teacher's words was to stop the
        // words being on this document. attendanceNotes/{sameId} is staff-only
        // and is written only when a caller actually supplies a note: a plain
        // status tap must not touch it, or it would wipe the alternate
        // step-in line that RosterPanel writes there.
        //
        // THE MARK GOES FIRST, and the order is the whole point. The note
        // collection carries no date guard, while the mark is refused outside
        // the ten-day teacher window. Writing the note first meant that on an
        // old mark the note write SUCCEEDED, the mark write was denied, and
        // the teacher was shown "could not save" while their previous words
        // had already been overwritten. Proved by a review against the built
        // rules. With the mark first, a refusal leaves the note untouched.
        const writeNote = async () => {
          // Re-checked inside the closure: the guard above narrows `db` for
          // the enclosing scope, not for a function called later.
          if (!db || input.notes === undefined) return;
          await setDoc(
            doc(db, 'attendanceNotes', id),
            {
              offeringId: input.offeringId,
              date: input.date,
              notes: input.notes.slice(0, 2000),
              updatedAt: serverTimestamp(),
            },
            { merge: false }
          );
        };
        if (exists) {
          await updateDoc(ref, {
            status: input.status,
            recordedBy: user.uid,
            updatedAt: serverTimestamp(),
          });
          await writeNote();
          return;
        }
        await setDoc(
          ref,
          {
            offeringId: input.offeringId,
            date: input.date,
            studentId: input.studentId,
            status: input.status,
            recordedBy: user.uid,
            recordedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: false }
        );
        await writeNote();
      })()
    );
    // The class now has attendance, so the "Attendance not marked" nag for
    // this class today is stale. Bobbie: "I clicked through and did attendance
    // but it still is showing up as a new message." attendanceNag.mjs keys its
    // notification deterministically (attendanceNag_{offeringId}_{todayKey}_{teacherUid})
    // and sends it to the teacher, so the teacher marking their own class can
    // mark it read - which is exactly what the deployed rule allows a
    // recipient to do. Best-effort and OUTSIDE the write above on purpose:
    // it must never make a successful mark look failed. It silently no-ops
    // when there is no nag (update on a missing doc), when it is already read,
    // or when the marker is not the nag's recipient (a sub or Director, who
    // are not the teacher the rule lets update it).
    //
    // THE STUDIO'S DAY ON BOTH SIDES. This gate used to ask `dayKey(new
    // Date())`, which is the DEVICE's calendar day, while `input.date` now
    // comes from the Attendance page's studio-day default. On the exact device
    // this matters for - a phone or installed PWA left on UTC, where 7:30pm
    // Central is already the 17th while the studio day is the 16th - the two
    // sides disagreed, the gate went FALSE, and the teacher's card sat there
    // until the hourly run. Which is her sentence three lines up, again.
    if (input.date === studioTodayKey(new Date())) {
      const nagRef = doc(db, 'notifications', `attendanceNag_${input.offeringId}_${input.date}_${user.uid}`);
      updateDoc(nagRef, { read: true, readAt: serverTimestamp() }).catch(() => {});
    }
  };
}

/**
 * Save the staff-only explanation for an attendance mark that already exists.
 *
 * A WHOLE-STRING WRITER, AND NO SCREEN USES IT ANY MORE (#1293 re-check, N3).
 * Several writers own lines of the same note (the absent answer, the late
 * reason, the cover line), and this replaces every one of them from a single
 * caller's copy. Use useSaveMergedAttendanceNote, which merges one writer's
 * line on the server's current text inside a transaction.
 *
 * The attendance metadata update and staff-only note commit atomically. The
 * update requires an existing mark and lets the deployed rule enforce its
 * correction window before either document changes. This does not alter
 * status and cannot create a mark, so a note blur racing a status choice
 * cannot put the dancer back to Late.
 */
export function useSaveAttendanceNote() {
  const { user } = useAuth();
  return async (input: {
    offeringId: string;
    date: string;
    studentId: string;
    notes: string;
  }): Promise<void> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    await withAttendanceWriteTimeout(
      (async () => {
        const id = attendanceDocId(input.offeringId, input.date, input.studentId);
        const attendanceRef = doc(db, 'attendance', id);
        const noteRef = doc(db, 'attendanceNotes', id);
        // The attendance update is the existing-record and correction-window
        // guard. Keeping it in the same batch as the staff-only note means a
        // refusal, or a Director clearing the mark at the same moment, cannot
        // leave an orphaned note that resurfaces on a later re-mark. This does
        // not alter status or recordedAt and cannot create a mark.
        const batch = writeBatch(db);
        batch.update(attendanceRef, {
          recordedBy: user.uid,
          updatedAt: serverTimestamp(),
        });
        batch.set(
          noteRef,
          {
            offeringId: input.offeringId,
            date: input.date,
            notes: input.notes.slice(0, 2000),
            updatedAt: serverTimestamp(),
          },
          { merge: false }
        );
        await batch.commit();
      })()
    );
  };
}

/**
 * Save ONE writer's line of a night's note, merged on the server's current
 * text inside a transaction (lib/attendanceNoteMerge.ts says why, and what it
 * costs: it needs signal). The absent note and the alternate step-in line
 * both save through this, so neither can write over the other.
 */
export function useSaveMergedAttendanceNote() {
  const { user } = useAuth();
  return async (input: {
    offeringId: string;
    date: string;
    studentId: string;
    merge: (current: string) => string | null;
  }): Promise<void> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    await saveMergedAttendanceNote(db, { uid: user.uid, ...input });
  };
}

/**
 * Hard-deletes one attendance mark. Bobbie, clearing test data: "these were
 * test info. can you delete these attendance recrods?" - there was no
 * affordance anywhere to do that.
 *
 * Director-only on screen (see canRemoveAttendanceMark in
 * lib/attendanceReportActions.ts) AND by rule: shared/firestore.rules'
 * attendance/{attendanceId} match block reads `allow delete: if
 * isDirector();`, so a non-Director calling this is denied at the rule, not
 * just hidden on screen. A client `if` is not a permission; this function
 * relies on that rule as the real gate.
 *
 * Targets the exact composite doc id (attendanceDocId), the same id every
 * mark is written and corrected under, so the delete can never land on the
 * wrong day or the wrong dancer.
 */
export async function deleteAttendanceMark(
  offeringId: string,
  date: string,
  studentId: string
): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  const id = attendanceDocId(offeringId, date, studentId);
  await deleteDoc(doc(db, 'attendance', id));
  // AND THE TEACHER'S NOTE WITH IT. The note is a separate document under the
  // same deterministic id, so deleting only the mark leaves an orphan - and
  // because the id is derived from offering, date and dancer, re-marking that
  // dancer on that day would RESURRECT the old note onto the new mark. Bobbie
  // deletes attendance for exactly this reason ("these were test info. can you
  // delete these attendance records?"), so a delete that leaves the words
  // behind is not a delete.
  //
  // Best effort and after the mark: a note that outlives its mark is
  // recoverable and invisible to families; a mark that outlives its delete is
  // the thing she asked to be rid of.
  try {
    await deleteDoc(doc(db, 'attendanceNotes', id));
  } catch {
    // Nothing to remove, or not permitted. The mark is gone either way.
  }
}

/**
 * Tally helper used by summary widgets (and the home card later).
 * Given a roster of student ids + their attendance map, returns a
 * count by status.
 */
export function tallyAttendance(
  studentIds: string[],
  byStudent: Map<string, AttendanceDoc>
): Record<AttendanceStatus | 'unmarked', number> {
  const tally: Record<AttendanceStatus | 'unmarked', number> = {
    present: 0,
    late: 0,
    absent: 0,
    unmarked: 0,
  };
  for (const sid of studentIds) {
    const rec = byStudent.get(sid);
    if (!rec) tally.unmarked++;
    else tally[rec.status]++;
  }
  return tally;
}

/**
 * React-friendly tally that recomputes only when inputs change.
 */
export function useAttendanceTally(
  studentIds: string[],
  byStudent: Map<string, AttendanceDoc>
) {
  return useMemo(
    () => tallyAttendance(studentIds, byStudent),
    [studentIds, byStudent]
  );
}

export interface StudentRecentAttendanceSummary {
  present: number;
  late: number;
  excused: number;
  absent: number;
  total: number;
  lastStatus: AttendanceStatus | null;
  lastDate: string | null;
}

export interface UseRecentClassAttendanceResult {
  loading: boolean;
  byStudent: Map<string, StudentRecentAttendanceSummary>;
  error: string | null;
}

function emptyRecentSummary(): StudentRecentAttendanceSummary {
  return {
    present: 0,
    late: 0,
    excused: 0,
    absent: 0,
    total: 0,
    lastStatus: null,
    lastDate: null,
  };
}

/**
 * Recent attendance summary for one class, keyed by studentId.
 * Teachers use this on the class workspace roster to spot patterns without
 * opening the full Director report.
 */
export function useRecentClassAttendance(
  offeringId: string | undefined,
  windowDays = 30
): UseRecentClassAttendanceResult {
  const [byStudent, setByStudent] = useState<Map<string, StudentRecentAttendanceSummary>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!db || !offeringId) {
      setByStudent(new Map());
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const cutoff = isoDaysAgo(windowDays);
    const q = query(
      collection(db, 'attendance'),
      where('offeringId', '==', offeringId),
      where('date', '>=', cutoff)
    );
    const unsub = onCountableMarks(
      q,
      cutoff,
      (snap) => {
        const next = new Map<string, StudentRecentAttendanceSummary>();
        for (const d of snap.docs) {
          const a = d.data() as Omit<AttendanceDoc, 'id'>;
          const status = normalizeAttendanceStatus(a.status);
          if (!status) continue;
          const cur = next.get(a.studentId) ?? emptyRecentSummary();
          cur[status] += 1;
          cur.total += 1;
          if (!cur.lastDate || a.date > cur.lastDate) {
            cur.lastDate = a.date;
            cur.lastStatus = status;
          }
          next.set(a.studentId, cur);
        }
        setByStudent(next);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [offeringId, windowDays]);

  return { loading, byStudent, error };
}

export interface UseTonightAttendanceMarksResult {
  /**
   * Map keyed by offeringId. A missing key means "not yet known" (the
   * listener has not reported back, or this offering was not part of what
   * the caller asked for) - the caller passes that straight through as a null
   * tally, and tonightAttendanceButtonState in lib/tonight.ts already treats
   * that the same as "no marks yet", so nothing else has to special-case a
   * missing entry.
   */
  byOffering: Map<string, TonightAttendanceMark[]>;
}

/**
 * One bounded onSnapshot listener PER tonight class (offeringId + today's
 * date), so the "Take attendance" button on the Tonight widget already
 * knows whether marks exist by the time a teacher expands that row, instead
 * of flashing "Take attendance" and flipping a beat later.
 *
 * Returns the ROWS, not a tally, on purpose. Whether the register is finished
 * is a question about the class's current roster, not about how many rows
 * exist, and the roster belongs to whoever is rendering the row: see
 * tonightAttendanceCountsForRoster in lib/tonight.ts, which the caller applies
 * with the offering in hand. Keeping the roster out of here also keeps the
 * listener keyed on class ids alone, so editing a roster does not tear down
 * and rebuild every tonight listener.
 *
 * `enabled` gates the whole thing off until the caller says it is safe to
 * start - Landing.tsx defers this a tick past first paint (perf round rule:
 * these queries must never sit on the home screen's critical path, same
 * spirit as the `enabled` gate on useAttendanceConcerns below). Bounded by
 * construction: `offeringIds` is capped upstream by the Tonight widget's
 * TONIGHT_VISIBLE constant while the list is collapsed (0-6 classes
 * tonight, not the whole studio) - packet J4 review, P2: it widens to
 * EVERY class still on plan.upcoming only once a viewer taps "+N more"
 * (tonightAttendanceQueryOfferingIds, lib/tonight.ts), because the
 * attendance-done checkmark has to reach a row the viewer can actually
 * see, and the in-place expand can show every class left tonight. Still
 * "tonight", never the whole studio, and each query only reads one
 * class's marks on one day either way.
 *
 * Same read rule as useAttendanceForClassDay above (isActiveStaffByDoc(),
 * resource-independent so a bounded query is provable) and the same
 * offeringId+date composite index - this just fans that same query shape
 * out to several classes at once instead of the one class the /attendance
 * page shows.
 */
export function useTonightAttendanceMarks(
  offeringIds: readonly string[],
  dateKey: string,
  enabled: boolean
): UseTonightAttendanceMarksResult {
  const [byOffering, setByOffering] = useState<Map<string, TonightAttendanceMark[]>>(new Map());
  // Stable key so the effect only restarts when the actual SET of ids
  // changes, not on every one-minute Tonight re-render where the array is a
  // fresh reference holding the same ids.
  const idsKey = offeringIds.join(',');

  useEffect(() => {
    if (!enabled || !db || offeringIds.length === 0) {
      setByOffering(new Map());
      return;
    }
    // Captured into a local so TS keeps the not-null narrowing inside the
    // .map() callback below - `db` is a module-level binding, so a closure
    // over it loses the narrowing the guard above just proved.
    const firestore = db;
    const unsubs = offeringIds.map((offeringId) => {
      const q = query(
        collection(firestore, 'attendance'),
        where('offeringId', '==', offeringId),
        where('date', '==', dateKey)
      );
      return onSnapshot(
        q,
        (snap) => {
          const marks: TonightAttendanceMark[] = [];
          for (const d of snap.docs) {
            const a = d.data() as Omit<AttendanceDoc, 'id'>;
            const status = normalizeAttendanceStatus(a.status);
            if (!status) continue;
            marks.push({ studentId: a.studentId, status });
          }
          setByOffering((prev) => {
            const next = new Map(prev);
            next.set(offeringId, marks);
            return next;
          });
        },
        () => {
          // A confirmation nicety on top of a button that still works via
          // plain navigation even if this listener never reports back - a
          // failed listener here stays silent rather than surfacing a
          // page-level error over a home-screen decoration.
        }
      );
    });
    return () => {
      for (const unsub of unsubs) unsub();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, dateKey, enabled]);

  return { byOffering };
}

// ---------------------------------------------------------------------------
// Report: aggregate attendance % per student (combined + per-class drill-in)
// ---------------------------------------------------------------------------

export interface ClassAttendanceBreakdown {
  offeringId: string;
  present: number;
  late: number;
  absent: number;
  /** Total marked records for this (student, class). */
  total: number;
  /** Showed up = present + late. */
  attendedRate: number; // 0..1
}

export interface StudentReportRow {
  studentId: string;
  combined: {
    present: number;
    late: number;
    absent: number;
    total: number;
    attendedRate: number; // 0..1, combined across classes
  };
  byClass: ClassAttendanceBreakdown[];
}

export interface UseAttendanceReportResult {
  loading: boolean;
  rows: StudentReportRow[];
  /** Studio-wide aggregate "showed up" %. */
  studioRate: number;
  error: string | null;
}

/**
 * Aggregate attendance across the last `windowDays` days and return one
 * row per student that has at least one marked record. Each row carries
 * the combined attendance rate AND the per-class breakdown so drill-in
 * is just an expand, no second query.
 *
 * "Attended" = present + late. Excused and absent are both "did not
 * attend" but counted separately so Bobbie can tell apart "kid was
 * sick, told us" from "kid ghosted us." Studio rate is computed across
 * all marked records (sum of student totals).
 */
export function useAttendanceReport(
  windowDays = 60,
  /**
   * Off means never open the listener. The attendance collection is staff-only
   * in the rules, so a family surface that mounts this hook unconditionally
   * fires a guaranteed permission denial; the comp board's dances tab needs it
   * for staff and must not ask for it as a parent. Defaults to on so no
   * existing caller changes behaviour.
   */
  enabled = true
): UseAttendanceReportResult {
  const [rows, setRows] = useState<StudentReportRow[]>([]);
  const [studioRate, setStudioRate] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setRows([]);
      setStudioRate(0);
      setError(null);
      setLoading(false);
      return;
    }
    if (!db) {
      setLoading(false);
      setError('Firestore not initialized.');
      return;
    }
    const cutoff = isoDaysAgo(windowDays);
    const q = query(
      collection(db, 'attendance'),
      where('date', '>=', cutoff)
    );
    const unsub = onCountableMarks(
      q,
      cutoff,
      (snap) => {
        // First pass: bucket by (studentId, offeringId).
        type Bucket = {
          present: number;
          late: number;
          absent: number;
        };
        const perStudentClass = new Map<string, Map<string, Bucket>>();
        for (const d of snap.docs) {
          const a = d.data() as Omit<AttendanceDoc, 'id'>;
          let byClass = perStudentClass.get(a.studentId);
          if (!byClass) {
            byClass = new Map();
            perStudentClass.set(a.studentId, byClass);
          }
          let b = byClass.get(a.offeringId);
          if (!b) {
            b = { present: 0, late: 0, absent: 0 };
            byClass.set(a.offeringId, b);
          }
          // normalizeAttendanceStatus folds a legacy cached 'excused' into
          // absent, which is also how it always counted here (it was never
          // part of attendedRate's numerator).
          const status = normalizeAttendanceStatus(a.status);
          if (status === 'present') b.present++;
          else if (status === 'late') b.late++;
          else if (status === 'absent') b.absent++;
        }

        // Build rows + studio totals.
        const out: StudentReportRow[] = [];
        let studioAttended = 0;
        let studioTotal = 0;
        for (const [studentId, byClass] of perStudentClass) {
          const breakdown: ClassAttendanceBreakdown[] = [];
          let cPresent = 0;
          let cLate = 0;
          let cAbsent = 0;
          for (const [offeringId, b] of byClass) {
            const total = b.present + b.late + b.absent;
            const attendedRate = total === 0 ? 0 : (b.present + b.late) / total;
            breakdown.push({
              offeringId,
              present: b.present,
              late: b.late,
              absent: b.absent,
              total,
              attendedRate,
            });
            cPresent += b.present;
            cLate += b.late;
            cAbsent += b.absent;
          }
          breakdown.sort((a, b) => a.attendedRate - b.attendedRate);
          const total = cPresent + cLate + cAbsent;
          const attendedRate = total === 0 ? 0 : (cPresent + cLate) / total;
          studioAttended += cPresent + cLate;
          studioTotal += total;
          out.push({
            studentId,
            combined: {
              present: cPresent,
              late: cLate,
              absent: cAbsent,
              total,
              attendedRate,
            },
            byClass: breakdown,
          });
        }
        // Lowest attendance % first so the people who need attention
        // surface first.
        out.sort((a, b) => a.combined.attendedRate - b.combined.attendedRate);
        setRows(out);
        setStudioRate(studioTotal === 0 ? 0 : studioAttended / studioTotal);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, windowDays]);

  return { loading, rows, studioRate, error };
}

/**
 * A dancer worth checking on, and WHY - one of Kim's two rules (see
 * attendanceConcernFor() in lib/alternates.ts), never a raw count with no
 * explanation attached. "3 total in a comp dance" and "2 Tuesdays in a row
 * of jazz" are different conversations, so `rule` and `reason` travel with
 * every flag instead of leaving the reader to guess which bar was cleared.
 */
export interface AttendanceConcern extends AttendanceConcernFlag {
  offeringId: string;
  studentId: string;
  /** Most recent date (ISO YYYY-MM-DD) we have any attendance record on file for this pair. */
  lastSeenDate: string;
  /** The teacher's absent note beside each miss that has one (Kim, 2026-09-28). */
  missNotes?: ConcernMissNote[];
  /** Every miss behind this concern carries a teacher's explanation. */
  explained?: boolean;
}

/**
 * A studio-day calendar key, `days` calendar days before today's studio day.
 *
 * Built from `studioTodayKey`'s own YYYY-MM-DD (never the device clock) and
 * then walked backwards in whole UTC calendar days off that key's own
 * numbers, so the studio's Central day never slides a day off in an evening
 * class under a UTC or Pacific device clock (the G1 audit finding: this fed
 * the Attendance Report window, the consecutive-absence cutoff and its
 * todayDate one day into the future between roughly 7pm and midnight
 * Central). Walking in UTC (not local) calendar math also keeps a DST
 * transition night from shifting the count by an hour into the wrong day.
 */
export function isoDaysAgo(days: number): string {
  const [y, m, da] = studioTodayKey(new Date()).split('-').map(Number);
  const shifted = new Date(Date.UTC(y, m - 1, da) - days * 24 * 60 * 60 * 1000);
  const yy = shifted.getUTCFullYear();
  const mm = String(shifted.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(shifted.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

export interface UseAttendanceConcernsResult {
  loading: boolean;
  /**
   * The concerns that still need somebody. A rec concern whose every miss the
   * teacher explained is NOT here (Kim, 2026-09-28: stop the check-in call
   * "when the teacher knows why they were gone"); it is in explainedConcerns.
   */
  concerns: AttendanceConcern[];
  /** Rec concerns the teacher fully explained. On the record, not a call. */
  explainedConcerns: AttendanceConcern[];
  error: string | null;
  /**
   * Epoch millis the server ran this scan at, or null before one has
   * answered. The list is as of this instant and does not move on its own, so
   * a surface showing it can say when it was taken.
   */
  scannedAt: number | null;
  /** Scan again now, bypassing the shared two minute cache. */
  refresh: () => void;
}

/** What one scan left behind. One state object, so a scan lands whole. */
interface ConcernScanState {
  loading: boolean;
  concerns: AttendanceConcern[];
  explainedConcerns: AttendanceConcern[];
  error: string | null;
  scannedAt: number | null;
}

/** A stable empty list, so a disabled consumer's memos do not churn. */
const EMPTY_CONCERNS: AttendanceConcern[] = [];

/**
 * NOT YET ASKED, which is loading:true and empty - never "no concerns". Both
 * the disabled state and the first render use this one object.
 */
const CONCERN_SCAN_IDLE: ConcernScanState = {
  loading: true,
  concerns: EMPTY_CONCERNS,
  explainedConcerns: EMPTY_CONCERNS,
  error: null,
  scannedAt: null,
};

export interface ConsecutiveWeekAbsence {
  studentId: string;
  /** How many calendar weeks in a row (ending this week or last week) had no present/late mark. */
  weeksMissed: number;
  /** Most recent date (ISO YYYY-MM-DD) this student was marked present or late, or null if none on file. */
  lastPresentDate: string | null;
}

export interface UseConsecutiveWeekAbsencesResult {
  loading: boolean;
  flags: ConsecutiveWeekAbsence[];
  error: string | null;
}

/**
 * Flags students who have gone `weeks` (default 3) or more CONSECUTIVE
 * calendar weeks in a row with an explicit absent or excused and nothing
 * else, across ALL of their classes combined, so Kim can check in with the
 * family before it turns into a bigger problem. This is distinct from
 * `useAttendanceConcerns`, which flags per (class, dancer) pair under Kim's
 * two rules; this hook looks studio-wide per student, and cares about an
 * unbroken run of missed weeks rather than a per-class count.
 *
 * Reuses the same `attendance` collection + `date >=` cutoff query pattern
 * as `useAttendanceConcerns`, just over a longer (~10 week) window so a
 * multi-week run can actually be observed. The counting itself lives in
 * consecutiveWeekAbsenceFlags in lib/attendanceIntegrity.ts, which is where
 * the honesty model and its tests are; see that file for what an unmarked
 * week does and why.
 *
 * Honest limitations, on purpose:
 * - A week only counts at all if one of this dancer's classes registered
 *   somebody. A week where none of them took attendance (holiday, staff
 *   forgot, closed for a break) is SKIPPED, because it says nothing about
 *   the dancer either way.
 * - A registered week with no row for THIS dancer is a hole, not a miss.
 *   The teacher may simply not have got to that name, and a flag that
 *   cannot tell those apart is a flag Kim cannot trust.
 * - "The student's classes" is inferred from the offering ids that show up
 *   on the student's own attendance records inside the query window, not
 *   from the live class roster. A brand-new class with no attendance taken
 *   yet simply cannot contribute a missed week yet either way.
 * - The query only looks back about (weeks + 7) calendar weeks. A student
 *   whose last present/late mark is older than that window will not show
 *   up with "attendance history before the gap" and will not be flagged.
 *   Widen the window if that starts to matter for a real studio year.
 * - Requires at least one attendance record (any status) dated before the
 *   start of the flagged missed-week run, so a brand-new student who has
 *   simply never been marked present yet is never mistaken for someone
 *   who has "gone missing."
 */
export function useConsecutiveWeekAbsences(
  weeks = 3
): UseConsecutiveWeekAbsencesResult {
  const [flags, setFlags] = useState<ConsecutiveWeekAbsence[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!db) {
      setLoading(false);
      setError('Firestore not initialized.');
      return;
    }
    const queryWeeks = weeks + 7;
    const cutoff = isoDaysAgo(queryWeeks * 7);
    const q = query(collection(db, 'attendance'), where('date', '>=', cutoff));
    const unsub = onCountableMarks(
      q,
      cutoff,
      (snap) => {
        const marks: StudioAttendanceMark[] = [];
        for (const d of snap.docs) {
          const a = d.data() as Omit<AttendanceDoc, 'id'>;
          const status = normalizeAttendanceStatus(a.status);
          if (!status) continue;
          marks.push({
            offeringId: a.offeringId,
            studentId: a.studentId,
            date: a.date,
            status,
          });
        }
        setFlags(
          consecutiveWeekAbsenceFlags({
            marks,
            weeks,
            queryWeeks,
            todayDate: isoDaysAgo(0),
          })
        );
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeks]);

  return { loading, flags, error };
}

/**
 * Surface (offeringId, studentId) pairs Kim should personally check on, per
 * her two rules: 3 misses TOTAL in a comp dance, or 2 misses in a ROW in one
 * rec class.
 *
 * THE SCAN RUNS ON THE SERVER NOW (2026-09-15). Bobbie, with a screenshot of
 * /attendance/concerns on her phone stuck on "Scanning attendance...":
 * "Loading super slowly or not at all." This hook used to open a live
 * onSnapshot on the whole `attendance` collection over a 120 day window. One
 * document exists per dancer per class per date, so that is thousands of
 * documents, and the Hub runs persistentLocalCache, so every one of them was
 * written into IndexedDB on the phone before the first snapshot could fire.
 * netlify/functions/attendanceConcerns.mjs does the same scan with the Admin
 * SDK and returns the short list; lib/attendanceConcernsClient.ts shares one
 * call between every surface that asks.
 *
 * THE RULES DID NOT MOVE, THEY WERE COPIED, and the copy is checked:
 * netlify/functions/lib/attendanceConcernsLocal.mjs carries the same
 * arithmetic (a missing row is never an absence, the rec rule counts
 * MEETINGS rather than elapsed days, a registered night with no row for this
 * dancer is a hole that breaks a run, the reinstatement baseline is
 * exclusive), and tests/attendance-concerns-parity.test.mjs runs it against
 * the real attendanceConcernFor, alternateCountModeFor,
 * alternateStreakBaseline, missRun, observeMeetings and totalMisses on shared
 * fixtures. See lib/attendanceIntegrity.ts for the model in full.
 *
 * WHAT WAS TRADED. The list no longer updates by itself while the page is
 * open: it is as of `scannedAt`, the page stamps that in studio time, and
 * `refresh()` scans again. Attendance is taken in a class, not while this
 * list is on screen, so a stamp plus a button is the honest trade for a page
 * that loads at all on a phone.
 *
 * `loading` stays true until the scan answers, AND while `enabled` is false:
 * the disabled state reports loading:true plus an empty list, so callers must
 * treat "disabled" as "NOT YET ASKED", never as "no concerns proven". `error`
 * surfaces a failed scan for the same reason: an empty list from a failed
 * read is not a clean bill of health.
 *
 * Director/staff-only by intent, anywhere else this would leak roster
 * patterns. The function refuses anyone who is not active staff, in the same
 * shape the attendance read rule uses; the UI still gates on the page level.
 */
export function useAttendanceConcerns(enabled = true): UseAttendanceConcernsResult {
  const [state, setState] = useState<ConcernScanState>(CONCERN_SCAN_IDLE);
  // Bumped by refresh(), and by the staleness check below. A counter rather
  // than a boolean so two taps in a row are two scans, not one and a no-op.
  const [askTick, setAskTick] = useState(0);
  // Whether the CURRENT tick was asked for by a person tapping Refresh. A
  // forced ask must skip the browser cache AND the server's shared one; a
  // staleness ask must not, or a tab regaining focus would re-bill the whole
  // window for every staff phone in the building.
  const forcedRef = useRef(false);
  const { user } = useAuth();
  const uid = user?.uid ?? null;

  useEffect(() => {
    // `enabled=false` keeps the call completely unmade so cheap surfaces (the
    // staff home banner) can defer it off the critical path. NOTE the
    // disabled state reports loading:true + empty - callers must treat
    // "disabled" as "not yet asked", never as "no concerns proven".
    if (!enabled) {
      setState(CONCERN_SCAN_IDLE);
      return;
    }
    let live = true;
    const forced = forcedRef.current;
    forcedRef.current = false;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    loadAttendanceConcerns({ force: forced, uid })
      .then((scan) => {
        if (!live) return;
        // ONE SPLIT, IN ONE PLACE, so every surface reading this hook (this
        // page, the Command Center, the staff home, the Director's heads-up)
        // agrees on what still needs a call. See lib/attendanceConcernExplained
        // .ts. NOT the teacher's own Heads Up: useTeacherHeadsUp counts from
        // the marks alone and still lists an explained rec dancer; whether it
        // should drop her too is open for Bobbie (#1293 refute round, F6).
        const split = splitExplainedConcerns(scan.concerns);
        setState({
          loading: false,
          concerns: split.needsCall,
          explainedConcerns: split.explained,
          error: null,
          scannedAt: scan.scannedAt,
        });
      })
      .catch((error: unknown) => {
        if (!live) return;
        // A FAILED SCAN NEVER BLANKS THE LIST. The first cut set concerns to
        // [] here, which drew the "Nothing to flag" star card underneath the
        // red error banner: the screen told Kim every dancer was fine at the
        // exact moment it could not tell. The doc comment above has always
        // said an empty list from a failed read is not a clean bill of
        // health; this is the line that makes the screen agree. Whatever was
        // last proven stays on screen, with its own stamp, and the error
        // rides beside it.
        setState((prev) => ({
          loading: false,
          concerns: prev.concerns,
          explainedConcerns: prev.explainedConcerns,
          error: error instanceof Error ? error.message : 'Attendance could not be scanned.',
          scannedAt: prev.scannedAt,
        }));
      });
    return () => {
      live = false;
    };
  }, [enabled, askTick, uid]);

  // A SNAPSHOT THAT SITS THERE FOR AN HOUR IS THE SAME LIE THIS CHANGE WAS
  // MEANT TO FIX. The Command Center and the staff home show this count with
  // no Refresh of their own, so a tab left open all evening would go on
  // claiming an hour-old number is current. Coming back to the tab re-asks
  // when what we hold is past the freshness window; inside it, this costs
  // nothing at all, and when it does ask, the server usually answers out of
  // its own shared scan without touching the attendance window.
  useEffect(() => {
    if (!enabled || typeof document === 'undefined') return;
    const recheck = () => {
      if (document.visibilityState === 'hidden') return;
      // A FIRST SCAN THAT FAILED has no stamp to go stale, so the staleness
      // gate alone never asked again: the Command Center tile, the staff home
      // banner and the comp dancers card have no Refresh of their own, and
      // one failed first answer stayed on them until a full reload (refute
      // round 1, F2). Coming back to the tab re-asks for that case too; the
      // server's single flight and rate limit bound what it costs.
      const failedFirstScan = state.scannedAt === null && state.error !== null && !state.loading;
      if (!failedFirstScan && !attendanceConcernsAreStale(state.scannedAt)) return;
      setAskTick((tick) => tick + 1);
    };
    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', recheck);
    return () => {
      window.removeEventListener('focus', recheck);
      document.removeEventListener('visibilitychange', recheck);
    };
  }, [enabled, state.scannedAt, state.error, state.loading]);

  const refresh = useCallback(() => {
    forcedRef.current = true;
    setAskTick((tick) => tick + 1);
  }, []);

  return {
    loading: !enabled || state.loading,
    concerns: enabled ? state.concerns : EMPTY_CONCERNS,
    explainedConcerns: enabled ? state.explainedConcerns : EMPTY_CONCERNS,
    error: enabled ? state.error : null,
    scannedAt: enabled ? state.scannedAt : null,
    refresh,
  };
}

/**
 * ONE DANCER'S OWN MARKS, for their family and for the dancer themselves.
 *
 * Bobbie, 2026-08-21: "We need both attendance and absence notices shown.
 * including late." Every other read in this file is a staff lane; this is the
 * family lane, and it exists because she decided a parent and the dancer
 * should see the teacher's own record, not just the absences the family
 * submitted.
 *
 * THE studentId FILTER IS A SECURITY BOUNDARY, not a convenience. The rule
 * (`shared/firestore.rules`, the attendance block) allows a read only when
 * `callerInStudentFamily(resource.data.studentId)`, and an UNFILTERED query
 * on this collection is refused outright for a family - proven in
 * tests/attendance-family-read-rules.test.mjs, which asserts both the
 * unfiltered denial and that this exact filtered shape is permitted. Widening
 * or dropping this `where` does not loosen a screen, it breaks the read.
 *
 * Bounded by date on purpose: an unbounded read here would grow for the life
 * of the account.
 */
export function useDancerAttendance(
  studentId: string | null | undefined,
  startDate: string,
  endDate: string
) {
  const [marks, setMarks] = useState<AttendanceDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!db || !studentId || !startDate || !endDate) {
      setMarks([]);
      setLoading(false);
      // Clear the error too. Without this an earlier failure's banner
      // outlives the condition that caused it: the dancer changes, the new
      // read never starts, and the card keeps telling a parent their
      // attendance could not load when nothing is being loaded at all.
      setError(null);
      return undefined;
    }
    setLoading(true);
    setError(null);
    const q = query(
      collection(db, 'attendance'),
      where('studentId', '==', studentId),
      where('date', '>=', startDate),
      where('date', '<', endDate)
    );
    const unsub = onCountableMarks(
      q,
      startDate,
      (snap) => {
        setMarks(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<AttendanceDoc, 'id'>) })));
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [studentId, startDate, endDate]);

  return { marks, loading, error };
}
